import React, { useEffect, useState } from "react";
import { api, inr, grams } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { DownloadSimple } from "@phosphor-icons/react";

function downloadCSV(rows, filename) {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(",")]
    .concat(
      rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(",")),
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function SalesReport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const load = async () => {
    const params = new URLSearchParams();
    if (from) params.set("from_date", from);
    if (to) params.set("to_date", to);
    const { data } = await api.get(`/reports/sales?${params.toString()}`);
    setData(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
            From
          </Label>
          <Input
            type="date"
            className="mt-1.5 bg-background border-border"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
            To
          </Label>
          <Input
            type="date"
            className="mt-1.5 bg-background border-border"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <Button
          onClick={load}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          Apply
        </Button>
        <Button
          variant="ghost"
          onClick={() => downloadCSV(data?.bills || [], "sales_report.csv")}
        >
          <DownloadSimple size={14} className="mr-1.5" /> Export CSV
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total Sales" value={inr(data?.total_sales)} />
        <Stat label="Total Collected" value={inr(data?.total_paid)} />
        <Stat label="Bills" value={data?.count ?? 0} />
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Invoice</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.bills || []).length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-8 text-muted-foreground"
                >
                  No bills.
                </TableCell>
              </TableRow>
            ) : (
              data.bills.map((b) => (
                <TableRow key={b.id}>
                  <TableCell className="font-mono text-xs">
                    {b.invoice_no}
                  </TableCell>
                  <TableCell>{(b.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell>{b.customer_name || "Walk-in"}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(b.grand_total)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(b.paid)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(b.balance)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StockReport() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/stock").then((r) => setData(r.data));
  }, []);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Items in stock" value={data?.total_items ?? 0} />
        <Stat label="Total weight" value={grams(data?.total_weight)} />
        <Stat label="Stock value" value={inr(data?.total_value)} />
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Net Wt</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Price</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.products || []).length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-8 text-muted-foreground"
                >
                  Inventory empty.
                </TableCell>
              </TableRow>
            ) : (
              data.products.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono text-xs">
                    {p.product_code}
                  </TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {grams(p.net_weight)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {p.quantity}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(p.sale_price)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function CustomerReport() {
  const [data, setData] = useState([]);
  useEffect(() => {
    api.get("/reports/customers").then((r) => setData(r.data));
  }, []);
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-secondary/40 hover:bg-secondary/40">
            <TableHead>Customer</TableHead>
            <TableHead>Mobile</TableHead>
            <TableHead className="text-right">Total Sales</TableHead>
            <TableHead className="text-right">Paid</TableHead>
            <TableHead className="text-right">Outstanding</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-center py-8 text-muted-foreground"
              >
                No data.
              </TableCell>
            </TableRow>
          ) : (
            data.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.mobile}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {inr(c.total_sales)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {inr(c.total_paid)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-gold">
                  {inr(c.outstanding)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-border bg-card p-4">
    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </div>
    <div className="font-display text-2xl mt-1 tabular-nums">{value}</div>
  </div>
);

export default function Reports() {
  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="reports-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Analytics
        </div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
          Reports
        </h1>
      </header>
      <Tabs defaultValue="sales" className="space-y-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="sales" data-testid="tab-sales">
            Sales
          </TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock">
            Stock
          </TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">
            Customers
          </TabsTrigger>
        </TabsList>
        <TabsContent value="sales">
          <SalesReport />
        </TabsContent>
        <TabsContent value="stock">
          <StockReport />
        </TabsContent>
        <TabsContent value="customers">
          <CustomerReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}
